import {Injectable} from '@angular/core';
import {Subject, Subscription, Observable} from 'rxjs';
import {filter, map, takeUntil} from 'rxjs/operators';

export enum Events {
  RequestDetailsSubmit = 'request-details-submit',
  RequestDetailsSave = 'request-details-save',
  TmReviewSave = 'tm-review-save',
  RequestDetailsDelete = 'request-details-delete',
  RequestDetailsValidation = 'request-details-submit-validation',
  ApproveSubmitValidated = 'approve-art-submit-validation',
  PoSowSubmitValidated = 'pow-sow-submit-validation',
  AtPrinterSubmitValidated = 'at-printer-submit-validation',
  DesignProcessValidation = 'design-process-validation',
  DesignProcessSubmit = 'design-process-submit',
  StepApprove = 'step-approve',
  StepSubmit = 'step-submit',
  StepCancel = 'step-cancel',
  StepBack = 'step-back',
  StepClose = 'step-close',
  StepActivate = 'step-activate',
  UploadArtworkSubmit = 'upload-submit',
  ChangeRequestStage = 'change-request-stage',
  StartRecalculatePoTotalPrice = 'start-recalculate-po-total-price',
  SowSubmit = 'sow-submit',
  PoSubmit = 'po-submit',
  TpsSubmit = 'tps-submit',
  UpdateActionHistory = 'update-action-history',
  UpdateCommentCount = 'update-comment-count',
  RequestDetails = 'requestDetails',
  PopFinancialServicesSubmitValidation = 'popFinancialServicesSubmitValidation',
  PopFinancialServicesSubmit = 'popFinancialServicesSubmit',
  PopFinancialServicesRollbackValidation = 'popFinancialServicesRollbackValidation',
  PopFinancialServicesRollback = 'popFinancialServicesRollback',
  FinancialServicesReview2Submit = 'financialServicesReview2Submit',
  ProductionReviewSubmit = 'production-review-submit',
  ProductionReviewRollback = 'production-review-rollback',
  ProductionReviewValidation = 'production-review-validation',
  PpVendorProofSubmit = 'pp-vendor-proof-submit',
  UploadPrinterProofValidation = 'upload-printer-proof-validation',
  UploadPrinterProofSubmit = 'upload-printer-proof-submit',
  TmReviewRollback = 'tm-review-rollback',
  RefreshPoApprovalList = 'refresh-po-approval-list'
}

export enum CardDesignEvents {
  TmReviewSubmitValidated = 'tm-review-submit-validation',
  TmReviewSubmit = 'tm-review-submit',
  TmReview2SubmitValidated = 'tm-review-2-submit-validation',
  TmReview2Submit = 'tm-review-2-submit',
  TmReview3Submit = 'tm-review-3-submit',
  CreativeReviewSubmitValidated = 'creative-review-submit-validation',
  CreativeReviewSubmit = 'creative-review-submit',
  IntExtClientProofApproveValidated = 'client-proof-approve-validation',
  IntExtClientProofApprove = 'client-proof-approve',
  PrepArtifactValidated = 'prep-artifact-validation',
  PrepArtifactSave = 'prep-artifact-save',
  PrepArtifactSubmit = 'prep-artifact-submit',
  PostArtifactSubmit = 'post-artifact-submit'
}

export enum PopDesignEvents {
  TmReviewSubmitValidated = 'tm-review-submit-validation',
  TmReviewSubmit = 'tm-review-submit',
  TmReview2SubmitValidated = 'tm-review-2-submit-validation',
  TmReview2Submit = 'tm-review-2-submit',
  TmReview3Submit = 'pop-tm-review-3-submit',
  FinancialServicesReview2Validation = 'financial-services-review-2-validation',
  FinancialServicesReview2Submit = 'financial-services-review-2-submit',
  PrepArtifactValidated = 'pop-prep-artifact-validation',
  PrepArtifactSave = 'pop-prep-artifact-save',
  PrepArtifactSubmit = 'pop-prep-artifact-submit',
  PostArtifactSubmit = 'pop-post-artifact-submit'
}

export enum PopProductionEvents {
  PrinterQuoteSubmitValidated = 'printer-quote-submit-validation',
  PrinterQuoteSubmit = 'printer-quote-submit',
  InternalRequesterReviewSubmitValidation = 'internal-requester-review-submit-validation',
  InternalRequesterReviewSubmit = 'internal-requester-review-submit',
  ReviewPrinterProofApproveValidation = 'review-printer-proof-approve-validation',
  ReviewPrinterProofApprove = 'review-printer-proof-approve'
}

export enum FsrEvents {
  UploadSubmit = 'fsr-upload',
  ArtworkApproveValidation = 'artwork-approve-validation',
  ArtworkApprove = 'artwork-approve'
}

export enum BifEvents {
  UploadArtworkValidation = 'upload-artwork-validation',
  UploadArtworkSubmit = 'upload-artwork-submit',
  InternalReviewPrinterProofValidated = 'internal-review-printer-proof-validation',
  InternalReviewPrinterProofApprove = 'internal-review-printer-proof-approve'
}

export enum ThirdPartyEvents {
  ArtworkApproveValidation = 'artwork-approve-validation',
  ArtworkApprove = 'artwork-approve',
  InternalExternalOperationsSave = 'internal-external-operations-save',
  InternalExternalOperationsValidation = 'internal-external-operations-validation',
  InternalExternalOperationsSubmit = 'internal-external-operations-submit',
  InternalExternalOperationsRollback = 'internal-external-operations-rollback',
  ConfirmPrinter = 'confirm-printer',
  ConfirmPrinterValidation = 'confirm-printer-validation',
  BulkChangeTps = 'bulk-change-tps'
}

export enum IhEvents {
  UploadArtworkValidation = 'upload-artwork-validation',
  UploadArtworkSubmit = 'upload-artwork-submit',
  ArtworkApproveValidation = 'artwork-approve-validation',
  ArtworkApprove = 'artwork-approve',
  InternalPrinterProofValidation = 'internal-printer-proof-validation',
  InternalPrinterProofApprove = 'internal-printer-proof-approve'
}
export enum ImageEvents {
  TmReviewSubmitValidated = 'tm-review-submit-validation',
  TmReviewSubmit = 'tm-review-submit',
  DesignProcessSubmitValidated = 'design-process-submit-validation',
  DesignProcessSubmit = 'design-process-submit'
}

export type RequestEvents =
  | Events
  | CardDesignEvents
  | PopDesignEvents
  | PopProductionEvents
  | FsrEvents
  | BifEvents
  | ThirdPartyEvents
  | IhEvents
  | ImageEvents;

interface EventObject<T = any> {
  eventName: string;
  value?: T;
}

@Injectable({
  providedIn: 'root'
})
export class EventService {
  private subject$ = new Subject();

  public emit<T = any>(event: EventObject<T>): void {
    this.subject$.next(event);
  }

  public on<T = any>(eventName: string, action: (value: T) => any, takeUntilBy: Observable<any> = new Subject<void>()): Subscription {
    return this.subject$
      .pipe(
        filter((e: EventObject) => e.eventName === eventName),
        map((e: EventObject) => e.value),
        takeUntil(takeUntilBy)
      )
      .subscribe(action);
  }
}
